// AddSlot — the ONE "+ Add" door (spec 48.24 §5/§7, signed-off mock add_slot_matrix_mock). Since the
// breadcrumbs are click-only (operator 2026-07-03), this door is also the home for lateral SWITCH.
//
// Rules (operator-locked 2026-07-02/03, spec 48.24 §7 · D1 · D9-v3):
//  · Creates nest UNDER THE NODE YOU'RE STANDING ON, offering only TYPES NOT ALREADY IN THE PATH
//    (D1: no eng-under-eng / no project-under-project; D9-v3: no client-under-client). The BE create_node
//    guard 422s a same-type-in-path create, so the menu MUST NOT offer one — a fully-nested project
//    (client›eng›project) therefore has zero creates and shows only "Apps — open here".
//  · Sections (all under ONE global search): Switch (siblings) · Create (types-not-in-path) · Go-to
//    (descendant subtree) · Apps (open here). Switch/Go-to navigate; Create/Apps act on the current node.
//  · Confidential toggle: a CLIENT defaults CHECKED (default-confidential, NOT permanent —
//    2026-07-03 ruling); engagement/project are the creator's choice. Approved Velocity users can
//    receive direct scope access at create-time whether the node is open or confidential.
//  · Retained while an app is open — the door is the only top-bar switch/create/go-to once crumbs are
//    click-only (it acts on the URL's current node).
import { useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { Plus, ChevronLeft, Check, X } from "lucide-react";
import { SUBAPPS, appCanMountAt, type ScopeLevel } from "@perlab/registry";
import { useToast } from "@perlab/ui";
import { navTo } from "./ContextChain";
import { nodeAncestry, useContextChain } from "../state/ContextChain";
import { useAppReadiness } from "../state/useAppReadiness";
import {
  addScopeMember,
  searchApprovedScopeUsers,
  type ApprovedScopeUser,
} from "../api/scopeMembers";
import { AppIconResolver } from "./AppIconResolver";
import { useEntitlements } from "../../lib/auth/EntitlementsContext";
import { placeAppInstance } from "../api/appInstances";

export type CreatableType = "client" | "engagement" | "project";
const CREATABLE: CreatableType[] = ["client", "engagement", "project"];
const TYPE_LABEL: Record<CreatableType, string> = { client: "Client", engagement: "Engagement", project: "Project" };

export function AddSlot({
  createType,
  parentNodeId,
  compact = false,
  onCreated,
}: {
  createType?: CreatableType;
  parentNodeId?: string;
  compact?: boolean;
  onCreated?: (nodeId: string) => void;
} = {}): ReactElement | null {
  const { scopeNodes, workspaceId, clientId, engagementId, projectId, createNodeUnder } = useContextChain();
  const { node: nodeParam } = useParams({ strict: false }) as { node?: string };
  const navigate = useNavigate();
  const { toast } = useToast();

  const { hasAppAccess } = useEntitlements();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [form, setForm] = useState<CreatableType | null>(createType ?? null); // null = root section view
  const [name, setName] = useState("");
  const [productUnit, setProductUnit] = useState("");
  const [businessUnit, setBusinessUnit] = useState("");
  const [confidential, setConfidential] = useState(createType === "client");
  const [members, setMembers] = useState<ApprovedScopeUser[]>([]);
  const [memberDraft, setMemberDraft] = useState("");
  const [memberResults, setMemberResults] = useState<ApprovedScopeUser[]>([]);
  const [busy, setBusy] = useState(false);
  const [mountingAppId, setMountingAppId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const rootRef = useRef<HTMLDivElement>(null);

  // The node you're standing on (a /n/$node route is authoritative; else the deepest typed scalar).
  const currentNodeId = parentNodeId || nodeParam || projectId || engagementId || clientId || workspaceId || "";
  const currentNode = scopeNodes.find((n) => n.id === currentNodeId);

  // Types already in the path (current node + ancestors) — same-type-in-path is EXCLUDED from Create
  // (spec D1/D9-v3; the BE create_node guard 422s it). A fully-nested project ⇒ zero creates (apps only).
  const pathTypes = useMemo(() => {
    if (!currentNode) return new Set<string>();
    return new Set<string>([currentNode.node_type, ...nodeAncestry(currentNodeId, scopeNodes).map((a) => a.node_type)]);
  }, [currentNodeId, currentNode, scopeNodes]);

  const level = (currentNode?.node_type ?? "workspace") as ScopeLevel;
  const openableApps = SUBAPPS.filter((a) => hasAppAccess(a.id) && appCanMountAt(a, level));
  // Honest data-readiness per openable app at the current node — shared probe, gated on `open` so it's
  // fetch-on-open (no always-on cost). loaded=data present · empty=no data · unknown=badge dropped.
  const appReadiness = useAppReadiness(openableApps, currentNodeId, open && !createType);

  // LEVEL-AWARE 'Go to' (operator 2026-07-03): only what's reachable UNDER the current node — its whole
  // SUBTREE (all descendant clients/engagements/projects: a client shows its engagements AND projects, an
  // engagement shows only its projects), matching the content Overview. SIBLINGS live in the Switch
  // section above (not here) — Go-to is DOWN the subtree. Search goes global (shownGoTo) to find any node.
  const goToNodes = useMemo(() => {
    if (!currentNode) return [];
    const isNav = (t: string) => t === "client" || t === "engagement" || t === "project";
    return scopeNodes.filter(
      (s) => isNav(s.node_type) && s.id !== currentNodeId && nodeAncestry(s.id, scopeNodes).some((a) => a.id === currentNodeId),
    );
  }, [currentNode, currentNodeId, scopeNodes]);

  // Searching is GLOBAL (find any node by name); browsing is state-aware (goToNodes above).
  const allNavNodes = useMemo(
    () => scopeNodes.filter((n) => (n.node_type === "client" || n.node_type === "engagement" || n.node_type === "project") && n.id !== currentNodeId),
    [scopeNodes, currentNodeId],
  );

  // SWITCH (operator 2026-07-03): the breadcrumbs are click-only now, so lateral switching lives in THIS
  // door — the current node's SIBLINGS (same parent + same type). Only rendered when a peer exists to
  // switch to; the current node is marked with a check. Covered by the one global search below.
  const switchNodes = useMemo(() => {
    const t = currentNode?.node_type;
    if (!currentNode || (t !== "client" && t !== "engagement" && t !== "project")) return [];
    return scopeNodes.filter((s) => s.parent_id === currentNode.parent_id && s.node_type === t);
  }, [currentNode, scopeNodes]);

  const reset = (): void => {
    setOpen(false); setQuery(""); setForm(createType ?? null); setName("");
    setProductUnit(""); setBusinessUnit(""); setConfidential(createType === "client");
    setMembers([]); setMemberDraft(""); setMemberResults([]); setExpanded(new Set());
  };

  // Close on outside-click / Escape (audit MED — the menu previously only closed via the Add button).
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent): void => { if (!rootRef.current?.contains(e.target as Node)) reset(); };
    const onKey = (e: KeyboardEvent): void => { if (e.key === "Escape") reset(); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onClick); document.removeEventListener("keydown", onKey); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => {
    let live = true;
    const term = memberDraft.trim();
    if (!form || term.length < 2) {
      setMemberResults([]);
      return () => { live = false; };
    }
    const timer = window.setTimeout(() => {
      searchApprovedScopeUsers(currentNodeId, term)
        .then((users) => {
          if (live) {
            setMemberResults(users.filter((user) => !members.some((m) => m.user_id === user.user_id)));
          }
        })
        .catch(() => { if (live) setMemberResults([]); });
    }, 200);
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [currentNodeId, form, memberDraft, members]);

  const TYPE_CHIP: Record<string, string> = { client: "C", engagement: "E", project: "P" };
  // Grouped 'Go to' (operator 2026-07-03): CLIENTS / ENGAGEMENTS / PROJECTS, each capped so a workspace
  // with 100s of nodes stays scannable — search narrows live, "see all N" expands one group inline
  // (the menu scrolls). CAP keeps the default compact.
  const GO_GROUPS: { type: CreatableType; label: string; plural: string }[] = [
    { type: "client", label: "Select client", plural: "clients" },
    { type: "engagement", label: "Select engagement", plural: "engagements" },
    { type: "project", label: "Select project", plural: "projects" },
  ];
  const GO_CAP = 6;

  // Retained even while an app is open (operator 2026-07-03) — the door is the ONLY top-bar way to
  // switch/create/go-to once the crumbs are click-only. Needs a resolved current node.
  if (!currentNodeId || !currentNode) return null;

  const startCreate = (t: CreatableType): void => {
    setForm(t);
    setName("");
    setProductUnit("");
    setBusinessUnit("");
    setConfidential(t === "client"); // client defaults ON (uncheckable — but NOT locked, 2026-07-03)
    setMembers([]);
    setMemberDraft(""); setMemberResults([]);
  };

  const chooseMember = (user: ApprovedScopeUser): void => {
    if (!members.some((member) => member.user_id === user.user_id)) {
      setMembers((current) => [...current, user]);
    }
    setMemberDraft("");
    setMemberResults([]);
  };

  const submit = async (): Promise<void> => {
    if (
      !form
      || !name.trim()
      || (form === "client" && (!productUnit.trim() || !businessUnit.trim()))
      || busy
    ) return;
    setBusy(true);
    try {
      const node = await createNodeUnder({
        parentId: currentNodeId,
        nodeType: form,
        name: name.trim(),
        confidential,
        ...(form === "client"
          ? { productUnit: productUnit.trim(), businessUnit: businessUnit.trim() }
          : {}),
      });
      // Grant direct access to approved users selected in the create form.
      if (members.length) {
        for (const m of members) {
          try { await addScopeMember(node.id, m.email); } catch (e) {
            toast(`Member "${m.email}" not added: ${e instanceof Error ? e.message : "error"}`, "danger");
          }
        }
      }
      toast(`Created ${form} "${node.name}"${confidential ? " · confidential" : ""}.`, "success");
      reset();
      onCreated?.(node.id);
    } catch (e) {
      toast(`Create failed: ${e instanceof Error ? e.message : "unknown error"}`, "danger");
    } finally {
      setBusy(false);
    }
  };

  const createRows = CREATABLE.filter((t) => !pathTypes.has(t)); // types-not-in-path (spec D1/D9-v3)
  const q = query.trim().toLowerCase();
  const shownGoTo = (q ? allNavNodes : goToNodes).filter((n) => !q || n.name.toLowerCase().includes(q));
  const shownCreate = createRows.filter((t) => !q || TYPE_LABEL[t].toLowerCase().includes(q));
  const shownApps = openableApps.filter((a) => !q || a.name.toLowerCase().includes(q));
  // Switch shows only when there's a sibling to move to (>1 incl. self); filtered by the global search.
  const shownSwitch = switchNodes.length > 1 ? switchNodes.filter((n) => !q || n.name.toLowerCase().includes(q)) : [];

  const goTo = (id: string): void => { reset(); navigate(navTo.nodeHome(workspaceId, id) as never); };

  const mountAndOpen = async (appId: string, appName: string): Promise<void> => {
    if (!currentNodeId || mountingAppId) return;
    setMountingAppId(appId);
    try {
      await placeAppInstance(appId, currentNodeId);
      reset();
      toast(`${appName} mounted.`, "success");
      navigate(navTo.nodeApp(workspaceId, currentNodeId, appId) as never);
    } catch (error) {
      toast(`Could not mount ${appName}: ${error instanceof Error ? error.message : "unknown error"}`, "danger");
    } finally {
      setMountingAppId(null);
    }
  };

  // Parent lineage (scope ancestors, root-first) shown under a Go-to/search row so same-named nodes under
  // DIFFERENT parents read apart (two "hh" under different clients). Same-parent identical siblings still
  // collide — that's a data issue (rename, or a future created-at field); FE nodes carry no distinguisher.
  const lineageOf = (nodeId: string): string =>
    nodeAncestry(nodeId, scopeNodes)
      .filter((a) => a.node_type === "client" || a.node_type === "engagement" || a.node_type === "project")
      .reverse()
      .map((a) => a.name)
      .join(" › ");
  // A same-type sibling with the same name already exists under the current node — soft warning at create
  // (non-blocking) so new indistinguishable duplicates aren't minted by accident.
  const dupSiblingName =
    form !== null && name.trim() !== "" &&
    scopeNodes.some(
      (s) => s.parent_id === currentNodeId && s.node_type === form && s.name.trim().toLowerCase() === name.trim().toLowerCase(),
    );

  return (
    <div ref={rootRef} className="relative inline-flex">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={createType ? `Add ${TYPE_LABEL[createType].toLocaleLowerCase()}` : undefined}
        title={createType ? `Add ${TYPE_LABEL[createType].toLocaleLowerCase()}` : undefined}
        onClick={() => (open ? reset() : setOpen(true))}
        className={compact
          ? "inline-flex size-8 items-center justify-center rounded-[var(--radius-control,4px)] border border-[var(--color-border-default)] text-[var(--color-text-secondary)] hover:border-[var(--color-border-strong)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-text-primary)]"
          : "inline-flex items-center gap-1 rounded-[2px] px-2 py-1 text-xs text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-text-primary)]"}
      >
        <Plus size={compact ? 16 : 13} aria-hidden /> {!compact && "Add"}
      </button>

      {open && (
        <div
          role="menu"
          className={`absolute top-[calc(100%+4px)] z-50 max-h-[75vh] w-[360px] overflow-y-auto rounded-[3px] border border-[var(--color-border-default)] bg-[var(--surface-sunken)] py-1 shadow-[var(--shadow-lg)] ${compact ? "right-0" : "left-0"}`}
        >
          {form === null ? (
            <>
              <div className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--color-text-secondary)]">
                Add to <span className="text-[var(--color-text-primary)]">{currentNode.name}</span>
              </div>
              <div className="border-t border-[var(--color-border-subtle)] px-2 py-1.5">
                <input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search existing or create…"
                  className="w-full rounded-[2px] border border-[var(--color-border-default)] bg-[var(--color-surface-base)] px-2 py-1 text-sm text-[var(--color-text-primary)] outline-none focus:border-[var(--color-accent)]"
                />
              </div>

              {/* Switch — lateral move to a SIBLING of the current node (same parent + type), since the
                  breadcrumbs are click-only. Current node marked; click a peer to switch to it. */}
              {shownSwitch.length > 0 && (
                <div className="border-t border-[var(--color-border-subtle)] py-1">
                  <div className="px-3 py-1 text-[10px] uppercase tracking-[0.08em] text-[var(--color-text-secondary)]">Switch {currentNode.node_type}</div>
                  {shownSwitch.map((n) => (
                    <button
                      key={n.id}
                      type="button"
                      role="menuitem"
                      aria-current={n.id === currentNodeId ? "true" : undefined}
                      onClick={() => (n.id === currentNodeId ? reset() : goTo(n.id))}
                      className={`flex w-full items-center gap-2 border-l-2 px-3 py-1.5 text-left text-sm text-[var(--color-text-primary)] hover:bg-[var(--color-surface-muted)] ${
                        n.id === currentNodeId
                          ? "border-[var(--color-accent)] bg-[var(--color-surface-muted)]"
                          : "border-transparent"
                      }`}
                    >
                      <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-[2px] bg-[var(--color-surface-muted)] text-[9px] text-[var(--color-text-secondary)]">{TYPE_CHIP[n.node_type]}</span>
                      <span className="min-w-0 flex-1 truncate">{n.name}</span>
                      {n.id === currentNodeId && <Check size={13} className="shrink-0 text-[var(--color-accent)]" aria-hidden />}
                    </button>
                  ))}
                </div>
              )}

              {/* Create new — a client / engagement / project UNDER the current node (confidential toggle).
                  Kept near the TOP (right under search) so it's never buried below a long Go-to list. */}
              {shownCreate.length > 0 && (
                <div className="border-t border-[var(--color-border-subtle)] py-1">
                  <div className="px-3 py-1 text-[10px] uppercase tracking-[0.08em] text-[var(--color-text-secondary)]">Create new</div>
                  {shownCreate.map((t) => (
                    <button
                      key={t}
                      type="button"
                      role="menuitem"
                      onClick={() => startCreate(t)}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[var(--color-accent)] hover:bg-[var(--color-surface-muted)]"
                    >
                      <Plus size={13} className="shrink-0" aria-hidden />
                      <span className="shrink-0 whitespace-nowrap font-medium">New {TYPE_LABEL[t].toLowerCase()}</span>
                      <span
                        className="ml-auto min-w-0 shrink truncate rounded-[3px] bg-[var(--color-surface-muted)] px-1.5 py-0.5 text-[10px] font-normal text-[var(--color-text-secondary)]"
                        title={`under ${currentNode.name}`}
                      >
                        under {currentNode.name}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {/* Go to — search/select an ALREADY-AVAILABLE node, GROUPED by type + capped so 100s stay
                  scannable. Each group: header w/ "shown of total", top GO_CAP, then "see all N" to expand. */}
              {GO_GROUPS.map((g) => {
                const rows = shownGoTo.filter((n) => n.node_type === g.type);
                if (rows.length === 0) return null;
                const isExp = expanded.has(g.type);
                const visible = isExp ? rows : rows.slice(0, GO_CAP);
                return (
                  <div key={g.type} className="border-t border-[var(--color-border-subtle)] py-1">
                    <div className="flex items-center justify-between px-3 py-1 text-[10px] uppercase tracking-[0.08em] text-[var(--color-text-secondary)]">
                      <span>{g.label}</span>
                      <span>{rows.length > GO_CAP ? `${visible.length} of ${rows.length}` : rows.length}</span>
                    </div>
                    {visible.map((n) => {
                      const lin = lineageOf(n.id);
                      return (
                        <button
                          key={n.id}
                          type="button"
                          role="menuitem"
                          onClick={() => goTo(n.id)}
                          className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-[var(--color-text-primary)] hover:bg-[var(--color-surface-muted)]"
                        >
                          <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-[2px] bg-[var(--color-surface-muted)] text-[9px] text-[var(--color-text-secondary)]">{TYPE_CHIP[n.node_type]}</span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate">{n.name}</span>
                            {lin && <span className="block truncate text-[10px] text-[var(--color-text-secondary)]">{lin}</span>}
                          </span>
                        </button>
                      );
                    })}
                    {rows.length > GO_CAP && !isExp && (
                      <button
                        type="button"
                        onClick={() => setExpanded((s) => new Set(s).add(g.type))}
                        className="flex w-full items-center gap-1 px-3 py-1 text-left text-[11px] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
                      >
                        › see all {rows.length} {g.plural}
                      </button>
                    )}
                  </div>
                );
              })}

              {/* Apps — open one here at the current node. Always shown in browse (empty hint if none
                  mount at this level), filtered while searching. */}
              {(!q || shownApps.length > 0) && (
                <div className="border-t border-[var(--color-border-subtle)] py-1">
                  <div className="px-3 py-1 text-[10px] uppercase tracking-[0.08em] text-[var(--color-text-secondary)]">Select app</div>
                  {shownApps.length === 0 ? (
                    <div className="px-3 py-1.5 text-xs italic text-[var(--color-text-secondary)]">No apps open at a {level}.</div>
                  ) : (
                    shownApps.map((a) => (
                      <button
                        key={a.id}
                        type="button"
                        role="menuitem"
                        onClick={() => void mountAndOpen(a.id, a.name)}
                        disabled={mountingAppId !== null}
                        className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-[var(--color-text-primary)] hover:bg-[var(--color-surface-muted)]"
                      >
                        <AppIconResolver name={a.icon} /> {a.name}
                        <span className="ml-auto flex shrink-0 items-center gap-2">
                          {appReadiness[a.id] === "ready" && (
                            <span className="inline-flex items-center gap-1 rounded-[3px] bg-[var(--color-surface-muted)] px-1.5 py-0.5 text-[10px] text-[var(--color-accent)]">● loaded</span>
                          )}
                          {appReadiness[a.id] === "needs" && (
                            <span className="inline-flex items-center gap-1 rounded-[3px] bg-[var(--color-surface-muted)] px-1.5 py-0.5 text-[10px] text-[var(--color-text-secondary)]">○ empty</span>
                          )}
                          {appReadiness[a.id] === "pending" && (
                            <span className="text-[10px] text-[var(--color-text-secondary)]">checking…</span>
                          )}
                          <span className="text-[11px] text-[var(--color-text-secondary)]">
                            {mountingAppId === a.id ? "mounting…" : "mount and open"}
                          </span>
                        </span>
                      </button>
                    ))
                  )}
                </div>
              )}

              {shownSwitch.length === 0 && shownGoTo.length === 0 && shownCreate.length === 0 && shownApps.length === 0 && (
                <div className="px-3 py-2 text-xs italic text-[var(--color-text-secondary)]">No matches.</div>
              )}
            </>
          ) : (
            <div className="px-3 py-2">
              {!createType && (
                <button
                  type="button"
                  onClick={() => setForm(null)}
                  className="mb-2 flex items-center gap-1 text-[11px] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
                >
                  <ChevronLeft size={12} aria-hidden /> Back
                </button>
              )}
              <div className="mb-2 text-sm font-medium text-[var(--color-text-primary)]">
                New {form} — in {currentNode.name}
              </div>
              <input
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") void submit(); }}
                aria-label={`${TYPE_LABEL[form]} name`}
                placeholder={`${TYPE_LABEL[form]} name`}
                className="w-full rounded-[2px] border border-[var(--color-border-default)] bg-[var(--color-surface-base)] px-2 py-1.5 text-sm text-[var(--color-text-primary)] outline-none focus:border-[var(--color-accent)]"
              />
              {form === "client" && (
                <div className="mt-2 grid gap-2">
                  <label className="text-xs text-[var(--color-text-primary)]">
                    Product Unit <span aria-hidden className="text-[var(--color-accent)]">*</span>
                    <input
                      required
                      maxLength={80}
                      value={productUnit}
                      onChange={(event) => setProductUnit(event.target.value)}
                      onKeyDown={(event) => { if (event.key === "Enter") void submit(); }}
                      aria-label="Product Unit"
                      className="mt-1 w-full rounded-[2px] border border-[var(--color-border-default)] bg-[var(--color-surface-base)] px-2 py-1.5 text-sm text-[var(--color-text-primary)] outline-none focus:border-[var(--color-accent)]"
                    />
                  </label>
                  <label className="text-xs text-[var(--color-text-primary)]">
                    Business Unit <span aria-hidden className="text-[var(--color-accent)]">*</span>
                    <input
                      required
                      maxLength={80}
                      value={businessUnit}
                      onChange={(event) => setBusinessUnit(event.target.value)}
                      onKeyDown={(event) => { if (event.key === "Enter") void submit(); }}
                      aria-label="Business Unit"
                      className="mt-1 w-full rounded-[2px] border border-[var(--color-border-default)] bg-[var(--color-surface-base)] px-2 py-1.5 text-sm text-[var(--color-text-primary)] outline-none focus:border-[var(--color-accent)]"
                    />
                  </label>
                  <p className="text-[10px] leading-4 text-[var(--color-text-secondary)]">
                    Required context for governed public market research in Client Intelligence.
                  </p>
                </div>
              )}
              {dupSiblingName && (
                <p className="mt-1.5 text-[11px] text-[var(--color-warning-500)]">
                  Another {form} named “{name.trim()}” already exists here — you can still create it, but they’ll be hard to tell apart.
                </p>
              )}
              <label className="mt-2.5 flex items-center gap-2 text-xs text-[var(--color-text-primary)]">
                <input type="checkbox" checked={confidential} onChange={(e) => setConfidential(e.target.checked)} />
                Confidential
                <span className="text-[var(--color-text-secondary)]">
                  {form === "client" ? "(default on — you can uncheck)" : "(seals contents + existence to members)"}
                </span>
              </label>

              <div className="mt-2 border-t border-[var(--color-border-subtle)] pt-2">
                <div className="text-[10px] uppercase tracking-[0.08em] text-[var(--color-text-secondary)]">Scope access</div>
                <div className="py-1 text-xs text-[var(--color-text-primary)]">
                  You (creator) <span className="text-[var(--color-text-secondary)]">· inherited access</span>
                </div>
                {members.map((member) => (
                  <div key={member.user_id} className="flex items-center justify-between gap-2 py-0.5 text-xs text-[var(--color-text-primary)]">
                    <span className="min-w-0 truncate">
                      {member.display_name || member.email}
                      <span className="text-[var(--color-text-secondary)]"> · {member.email}</span>
                    </span>
                    <button
                      type="button"
                      aria-label={"Remove " + member.email}
                      onClick={() => setMembers((current) => current.filter((user) => user.user_id !== member.user_id))}
                      className="text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
                    >
                      <X size={11} aria-hidden />
                    </button>
                  </div>
                ))}
                <div className="relative mt-1">
                  <input
                    value={memberDraft}
                    role="combobox"
                    aria-autocomplete="list"
                    aria-expanded={memberResults.length > 0}
                    onChange={(e) => setMemberDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && memberResults[0]) {
                        e.preventDefault();
                        chooseMember(memberResults[0]);
                      }
                    }}
                    placeholder="Search approved users by name or email…"
                    className="w-full rounded-[2px] border border-[var(--color-border-default)] bg-[var(--color-surface-base)] px-2 py-1 text-xs text-[var(--color-text-primary)] outline-none focus:border-[var(--color-accent)]"
                  />
                  {memberResults.length > 0 && (
                    <ul role="listbox" className="absolute z-20 mt-1 max-h-40 w-full overflow-auto rounded-[2px] border border-[var(--color-border-default)] bg-[var(--color-surface-base)] shadow-lg">
                      {memberResults.map((user) => (
                        <li key={user.user_id} role="option">
                          <button
                            type="button"
                            onClick={() => chooseMember(user)}
                            className="w-full px-2 py-1.5 text-left hover:bg-[var(--color-surface-muted)]"
                          >
                            <span className="block truncate text-xs text-[var(--color-text-primary)]">{user.display_name || user.email}</span>
                            <span className="block truncate text-[10px] text-[var(--color-text-secondary)]">{user.email} · {user.platform_role}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <button
                type="button"
                disabled={
                  !name.trim()
                  || (form === "client" && (!productUnit.trim() || !businessUnit.trim()))
                  || busy
                }
                onClick={() => void submit()}
                className="mt-3 inline-flex items-center gap-1 rounded-[2px] bg-[var(--color-accent)] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
              >
                <Check size={12} aria-hidden /> Create
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
